import { CheckCard, ProList, ProListProps } from '@ant-design/pro-components';
import { Empty, TablePaginationConfig, Typography } from 'antd';
import { FC, Key, ReactElement, ReactNode, memo } from 'react';
import { ArrowRight } from 'react-bootstrap-icons';
import { Link } from 'react-router-dom';

import './AppCardList.scss';

const { Paragraph } = Typography;

interface Props<T extends Record<string, unknown>> {
  list: T[];
  headerTitle: ProListProps['headerTitle'];
  pagination?: Pick<TablePaginationConfig, 'current' | 'total' | 'pageSize' | 'disabled' | 'onChange'>;
  grid?: ProListProps<T>['grid'];
  rowKey: keyof T;
  titleKey: keyof T;
  selectedKey?: Key;
  classNames?: (props: T) => {
    cardClassName?: ProListProps['className'];
  };
  content?: (props: T) => ReactNode;
  titleRender?: (title: string, record: T) => ReactElement;
  onClick?: (record: T) => void;
  navigateTo?: (record: T) => {
    to: string | undefined;
  };
  emptyPlaceholder?: ReactNode;
}

export const AppCardList: <T extends Record<string, unknown>>(props: Props<T>) => ReactElement<Props<T>> = ({
  list,
  pagination,
  rowKey,
  titleKey,
  headerTitle,
  selectedKey,
  grid,
  emptyPlaceholder,
  classNames,
  navigateTo,
  titleRender,
  content,
  onClick
}) => {
  selectedKey = selectedKey ? selectedKey.toString().split('/')[0] : selectedKey;

  return (
    <ProList
      cardProps={{ bodyStyle: { padding: 0 } }}
      className="app-card-list"
      headerTitle={ headerTitle }
      pagination={
        pagination
          ? {
            showSizeChanger: false,
            showQuickJumper: true,
            showTotal: (total, range) => `${ range[0] }-${ range[1] } з ${ total } елементів`,
            ...pagination
          }
          : undefined
      }
      rowKey={ (record) => record[rowKey] as Key }
      rowSelection={{
        selectedRowKeys: selectedKey ? [selectedKey] : [],
        type: 'radio',
        alwaysShowAlert: false,
      }}
      grid={
        grid || {
          gutter: [16, 0],
          xs: 1,
          sm: 1,
          md: 1,
          lg: 1,
          xl: 2,
          xxl: 2
        }
      }
      locale={{
        emptyText: emptyPlaceholder || <Empty description="Даних немає" image={ Empty.PRESENTED_IMAGE_SIMPLE }/>,
      }}
      renderItem={ (record) => (
        <div
          className="ant-pro-list-row-card"
          key={ String(record.id) }
        >
          <CheckCardContainerHoc
            card={
              <CheckCardComponent
                className={ classNames?.(record)?.cardClassName }
                record={ record }
                selectedKey={ selectedKey }
                titleKey={ titleKey }
                titleRender={ titleRender }
                content={ content }
                onClick={ onClick }
              />
            }
            navigateTo={ navigateTo?.(record)?.to }
          />
        </div>
      ) }
      dataSource={ list }
    />
  );
};

const CheckCardComponent: <T extends Record<string, unknown>>(props: Pick<
  Props<T>,
  'selectedKey' | 'titleKey' | 'titleRender' | 'content' | 'onClick'
  > & { record: T; className: string | undefined; }
) => ReactElement<Props<T>> = ({
  record,
  selectedKey,
  titleKey,
  className,
  titleRender,
  content,
  onClick,
}) => {
  return (
    <CheckCard
      defaultChecked={ record.id === selectedKey }
      style={{ width: '100%', cursor: 'pointer' }}
      checked={ record.id === selectedKey }
      className={ className }
      onClick={ () => {
        onClick?.(record);
      } }
      title={ <>
        <span className="app-card-list__item-arrow"><ArrowRight size={ 22 }/></span>
        <Typography.Title style={{ margin: 0 }} level={ 5 }>
          <Paragraph
            style={{ marginBottom: 0, fontSize: 'inherit' }}
            ellipsis={{
              rows: 1,
              tooltip: true
            }}
          >
            {
              titleRender
                ? titleRender(record[(titleKey).toString()] as string, record)
                : String(record[(titleKey)])
            }
          </Paragraph>
        </Typography.Title>
      </> }
    >
      { content?.(record) ?? <></> }
    </CheckCard>
  );
};

const CheckCardContainerHoc: FC<{
  card: ReactElement
  navigateTo?: string | undefined;
}> = memo(({ navigateTo, card }) => {
  if (navigateTo) {
    return <Link to={ navigateTo }>{ card }</Link>;
  } else {
    return card;
  }
});
